import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Paperclip, Upload, Trash2, FileText, Download, Eye } from "lucide-react";
import { api, errMsg, API } from "../lib/api";
import { useAuth } from "../lib/auth";

const mb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const dt = (s) => new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" });

const sendFile = async (uid, file, category, note, onPct) => {
  const s = await api.post(`/orders/${uid}/files/start`, { name: file.name, size: file.size, category, note });
  const { upload_id, chunk_size } = s.data;
  for (let i = 0, off = 0; off < file.size; i += 1, off += chunk_size) {
    await api.put(`/order-uploads/${upload_id}/chunk`, file.slice(off, off + chunk_size), { params: { index: i }, headers: { "Content-Type": "application/octet-stream" } });
    onPct(Math.min(99, Math.round(((off + chunk_size) / file.size) * 100)));
  }
  return api.post(`/order-uploads/${upload_id}/finish`);
};

const INLINE = ["pdf", "png", "jpg", "jpeg", "webp"];
const fileUrl = (uid, f, dl) => `${API}/orders/${encodeURIComponent(uid)}/files/${f.file_id}/download${dl ? "?download=1" : ""}`;

const notFull = (name, msg) => toast.error(`"${name}" wasn't uploaded fully`, { description: msg || "Please upload it again.", duration: 12000 });

const Uploader = ({ uid, cats, maxMb, onDone }) => {
  const ref = useRef(null);
  const [cat, setCat] = useState("drawing");
  const [note, setNote] = useState("");
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > maxMb * 1048576) { toast.error(`Files can be up to ${maxMb} MB`); return; }
    setPct(0);
    try { await sendFile(uid, file, cat, note, setPct); toast.success(`${file.name} uploaded`); setNote(""); onDone(); }
    catch (x) { notFull(file.name, errMsg(x)); } finally { setPct(null); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <select data-testid="uid-file-category" value={cat} onChange={(e) => setCat(e.target.value)} className="bg-muted/60 border h-9 px-2 text-sm">
        {Object.entries(cats).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <input data-testid="uid-file-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional), e.g. Rev 2 approved" className="bg-muted/60 border h-9 px-2 text-sm flex-1 min-w-[180px]" />
      <input ref={ref} type="file" className="hidden" data-testid="uid-file-input" onChange={pick} accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,.docx,.doc,.pptx,.ppt,.dwg,.dxf,.zip" />
      <button data-testid="uid-file-upload-button" disabled={pct !== null} onClick={() => ref.current?.click()} className="bg-primary text-primary-foreground h-9 px-4 text-sm flex items-center gap-2 disabled:opacity-60">
        <Upload className="h-4 w-4" />{pct !== null ? `Uploading ${pct}%` : "Upload file"}</button>
      {pct !== null && <div className="w-full h-1 bg-muted overflow-hidden" data-testid="uid-file-progress"><div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} /></div>}
    </div>
  );
};

const seenText = (s) => s.people.map((x) => `${x.name}${x.times > 1 ? ` ×${x.times}` : ""}`).join(", ") + (s.people_count > s.people.length ? ` +${s.people_count - s.people.length} more` : "");

const SeenLine = ({ uid, f }) => (
  <div className="text-[11px] pl-8 pb-2 -mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`uid-file-seen-${f.file_id}`}>
    <Eye className="h-3 w-3 text-primary shrink-0" />
    {f.seen.opens ? <span>Seen by {seenText(f.seen)} · last {dt(f.seen.people[0].last_at)}</span> : <span className="text-muted-foreground">Not opened yet</span>}
    <Link to={`/orders/document-log?uid=${encodeURIComponent(uid)}`} className="text-primary hover:underline" data-testid={`uid-file-seen-log-${f.file_id}`}>View log</Link>
  </div>
);

const FileRow = ({ uid, f, onDone, onOpened }) => {
  const opened = () => { if (f.seen) setTimeout(onOpened, 1500); };
  const remove = () => {
    if (!window.confirm(`Remove ${f.name} from this UID?`)) return;
    api.post(`/orders/${uid}/files/${f.file_id}/remove`).then(() => { toast.success("File removed"); onDone(); }).catch((e) => toast.error(errMsg(e)));
  };
  const body = (<>
    <div className="text-sm truncate">{f.name}</div>
    <div className="text-[11px] text-muted-foreground">{f.category_label}{f.version > 1 ? ` · version ${f.version}` : ""} · {mb(f.size)} · {f.uploaded_by_name} · {dt(f.uploaded_at)}{f.note ? ` · ${f.note}` : ""}</div>
    {f.broken && <div className="text-[11px] text-rose-600" data-testid={`uid-file-broken-${f.file_id}`}>This file was saved incompletely. Please upload it again.</div>}
  </>);
  return (
    <div className="border-b last:border-0">
    <div className="flex items-center gap-3 py-2" data-testid={`uid-file-${f.file_id}`}>
      <FileText className="h-5 w-5 text-primary shrink-0" />
      {f.broken ? <button type="button" onClick={() => notFull(f.name)} className="text-left min-w-0 flex-1" data-testid={`uid-file-open-${f.file_id}`}>{body}</button> : (
        <a href={fileUrl(uid, f, !INLINE.includes(f.ext))} target={INLINE.includes(f.ext) ? "_blank" : undefined} rel="noopener noreferrer" onClick={opened} className="text-left min-w-0 flex-1 hover:text-primary" data-testid={`uid-file-open-${f.file_id}`}>{body}</a>)}
      {!f.broken && <a href={fileUrl(uid, f, true)} onClick={opened} title="Download" data-testid={`uid-file-download-${f.file_id}`} className="text-muted-foreground hover:text-primary p-1"><Download className="h-4 w-4" /></a>}
      {f.can_remove && <button data-testid={`uid-file-remove-${f.file_id}`} onClick={remove} title="Remove" className="text-muted-foreground hover:text-rose-500 p-1"><Trash2 className="h-4 w-4" /></button>}
    </div>
    {f.seen && <SeenLine uid={uid} f={f} />}
    </div>
  );
};

export const OrderFiles = ({ uid, onChanged }) => {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/orders/${uid}/files`).then((r) => setD(r.data)).catch(() => {}), [uid]);
  const changed = () => { load(); onChanged?.(); };
  useEffect(() => { load(); }, [load]);
  const broken = d ? d.items.filter((f) => f.broken).length : 0;
  useEffect(() => {
    if (broken) toast.warning(`${broken} file${broken > 1 ? "s" : ""} on ${uid} ${broken > 1 ? "weren't" : "wasn't"} uploaded fully`, { id: `broken-${uid}`, description: "Please upload again.", duration: 12000 });
  }, [broken, uid]);
  if (!d) return null;
  return (
    <div className="ax-panel p-5 mb-6" data-testid="uid-files-panel">
      <div className="flex items-center gap-2 mb-1"><Paperclip className="h-4 w-4 text-primary" /><h3 className="font-display text-base">Files</h3><span className="text-xs text-muted-foreground">({d.items.length})</span></div>
      <p className="text-xs text-muted-foreground mb-3">Drawings, review notes and other files are open to everyone who can open this UID. Internal costs are only for founders, finance and branch heads. PO and estimate documents are only for sales, finance, the execution team and the production and operations heads. Up to {d.max_mb} MB per file.</p>
      {d.docs_hidden && <p className="text-xs text-amber-800 mb-3" data-testid="uid-files-docs-hidden">PO and estimate documents on this UID are hidden for your role.</p>}
      {can("orders", "update") && <Uploader uid={uid} cats={d.categories} maxMb={d.max_mb} onDone={changed} />}
      {d.items.length ? d.items.map((f) => <FileRow key={f.file_id} uid={uid} f={f} onDone={changed} onOpened={load} />)
        : <div className="text-sm text-muted-foreground py-3" data-testid="uid-files-empty">No files yet.</div>}
    </div>
  );
};
